import { useEffect, useState, type ReactNode } from 'react';
import { ArrowRight, CalendarDays, CheckCircle2, FileText, Package, ThumbsUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/features/auth';
import { NotificationsWidget } from '@/features/notifications';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { loadPendingApprovalJobs } from '@/features/approvals/api';
import { listExtrasMonth, listInvoiceDrafts } from '@/features/planning/api';
import { type Language, type ViewState } from '@/types';

// ── Types ──────────────────────────────────────────────────────────────────

interface TodayJob {
  id: string;
  site_id: string;
  site_name: string;
  cleaner_name: string | null;
  status: string;
  scheduled_for: string;
}

interface KPIs {
  pendingApprovals: number;
  pendingExtras: number;
  invoicedThisMonthSek: number;
  completedLast7Days: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function sevenDaysAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
}

function formatSek(value: number): string {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(value);
}

function jobStatusBadge(status: string) {
  if (status === 'completed') return <Badge variant="success">Slutförd</Badge>;
  if (status === 'approved') return <Badge variant="success">Godkänd</Badge>;
  if (status === 'cancelled') return <Badge variant="destructive">Avbokad</Badge>;
  return <Badge variant="outline">Tilldelad</Badge>;
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string; key?: unknown }) {
  return <div className={cn('animate-pulse rounded bg-muted', className)} />;
}

// ── Today Strip ────────────────────────────────────────────────────────────

interface TodayStripProps {
  jobs: TodayJob[];
  loading: boolean;
}

function TodayStrip({ jobs, loading }: TodayStripProps) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Idag</h2>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-24 w-48 shrink-0" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Inga jobb idag.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {jobs.map((job) => (
            <button
              key={job.id}
              type="button"
              onClick={() => {
                window.location.hash = `/brfs/${job.site_id}?tab=jobs`;
              }}
              className="shrink-0 w-52 text-left rounded-lg border bg-card p-3 shadow-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <p className="font-medium text-sm leading-tight line-clamp-1">{job.site_name}</p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {job.cleaner_name ?? 'Ej tilldelad'}
              </p>
              <div className="mt-2">{jobStatusBadge(job.status)}</div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ── KPI Card ───────────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: string | number;
  loading: boolean;
  onClick?: () => void;
}

function KpiCard({ icon, label, value, loading, onClick }: KpiCardProps) {
  return (
    <Card
      className={cn('shadow-sm transition-colors', onClick && 'cursor-pointer hover:bg-muted/40')}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="text-muted-foreground">{icon}</div>
          {onClick && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50" />}
        </div>
        {loading ? (
          <>
            <Skeleton className="h-7 w-16 mt-3" />
            <Skeleton className="h-3.5 w-24 mt-2" />
          </>
        ) : (
          <>
            <p className="text-2xl font-bold mt-3 tabular-nums">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Copy ───────────────────────────────────────────────────────────────────

const copy = {
  sv: { welcome: 'Välkommen tillbaka,' },
  en: { welcome: 'Welcome back,' },
} as const;

// ── Dashboard ──────────────────────────────────────────────────────────────

interface DashboardProps {
  language: Language;
  onNavigate: (view: ViewState) => void;
}

export function Dashboard({ language, onNavigate }: DashboardProps) {
  const { profile, session } = useAuth();
  const token = session?.access_token ?? null;
  const text = copy[language];
  const displayName = profile?.full_name || 'User';

  // Today's jobs
  const [todayJobs, setTodayJobs] = useState<TodayJob[]>([]);
  const [todayLoading, setTodayLoading] = useState(true);

  // KPIs
  const [kpis, setKpis] = useState<KPIs>({ pendingApprovals: 0, pendingExtras: 0, invoicedThisMonthSek: 0, completedLast7Days: 0 });
  const [kpiLoading, setKpiLoading] = useState(true);

  // Fetch today's jobs from Supabase directly
  useEffect(() => {
    const today = todayIso();

    supabase
      .from('jobs')
      .select('id, site_id, scheduled_for, status, assigned_user_id, snapshot')
      .eq('scheduled_for', today)
      .neq('status', 'cancelled')
      .order('scheduled_for')
      .then(({ data, error }) => {
        if (error || !data) {
          setTodayLoading(false);
          return;
        }

        const mapped: TodayJob[] = data.map((row) => {
          const snap = row.snapshot as Record<string, unknown> | null;
          const siteName =
            (typeof snap?.site_name === 'string' ? snap.site_name : null) ??
            row.site_id;
          const cleanerName =
            typeof snap?.assigned_user_name === 'string'
              ? snap.assigned_user_name
              : null;

          return {
            id: row.id,
            site_id: row.site_id,
            site_name: siteName,
            cleaner_name: cleanerName,
            status: row.status,
            scheduled_for: row.scheduled_for,
          };
        });

        setTodayJobs(mapped);
        setTodayLoading(false);
      });
  }, []);

  // Fetch all KPIs
  useEffect(() => {
    if (!token) {
      setKpiLoading(false);
      return;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    const sevenAgo = sevenDaysAgoIso();

    Promise.allSettled([
      // Pending approvals
      loadPendingApprovalJobs(token),
      // Pending extras for this month
      listExtrasMonth({ month: monthStr }, token),
      // Invoice drafts this month
      listInvoiceDrafts(year, month, token),
      // Last 7 days completed jobs
      supabase
        .from('jobs')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'completed')
        .gte('completed_at', sevenAgo),
    ]).then(([approvalsRes, extrasRes, invoicesRes, completedRes]) => {
      const pendingApprovals =
        approvalsRes.status === 'fulfilled'
          ? approvalsRes.value.filter((j) => j.status === 'completed').length
          : 0;

      const pendingExtras =
        extrasRes.status === 'fulfilled'
          ? extrasRes.value.grandTotals.byStatus['assigned'] ?? 0
          : 0;

      const invoicedThisMonthSek =
        invoicesRes.status === 'fulfilled'
          ? invoicesRes.value
              .filter((d) => d.status !== 'skipped')
              .reduce((sum, d) => sum + (d.total_ex_moms ?? 0), 0)
          : 0;

      const completedLast7Days =
        completedRes.status === 'fulfilled' && completedRes.value.count != null
          ? completedRes.value.count
          : 0;

      setKpis({ pendingApprovals, pendingExtras, invoicedThisMonthSek, completedLast7Days });
      setKpiLoading(false);
    });
  }, [token]);

  return (
    <div className="space-y-6 animate-fade-in pb-4">
      {/* Greeting */}
      <header className="space-y-1">
        <h1 className="text-3xl font-bold text-foreground">
          {text.welcome} {displayName}
        </h1>
      </header>

      {/* Notifications */}
      <NotificationsWidget language={language} onOpenAll={() => onNavigate('notifications')} />

      {/* Today's schedule strip */}
      <TodayStrip jobs={todayJobs} loading={todayLoading} />

      {/* KPI grid */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Nyckeltal</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiCard
            icon={<ThumbsUp className="h-4 w-4" />}
            label="Väntar godkännande"
            value={kpis.pendingApprovals}
            loading={kpiLoading}
            onClick={() => onNavigate('approvals')}
          />
          <KpiCard
            icon={<Package className="h-4 w-4" />}
            label="Extra, ej godkända"
            value={kpis.pendingExtras}
            loading={kpiLoading}
            onClick={() => onNavigate('extras')}
          />
          <KpiCard
            icon={<FileText className="h-4 w-4" />}
            label={`Fakturerat ${new Date().toLocaleString('sv-SE', { month: 'long' })} (ex moms)`}
            value={formatSek(kpis.invoicedThisMonthSek)}
            loading={kpiLoading}
            onClick={() => onNavigate('invoicing')}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-4 w-4" />}
            label="Slutförda, 7 dagar"
            value={kpis.completedLast7Days}
            loading={kpiLoading}
            onClick={() => onNavigate('approvals')}
          />
        </div>
      </section>
    </div>
  );
}
